import { useState, useRef, useEffect, useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import axios from 'axios';
import { getServerErrorMessage } from '../../utils/apiErrorMessage';
import { useNavigate } from 'react-router-dom';
import { SessionManager } from '../../utils/SessionManager';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { useDownloadProgress } from '../../contexts/DownloadProgressProvider';
import { SnackbarType } from '../Snackbar';
import { VideoCardSideProps, VideoImage, DialogConfig, UseVideoCardSideReturn } from './types';
import { useKebabMenu, UseKebabMenuReturn } from '../common/KebabMenu';
import { UI } from '../../config/constants';
import { downloadVideo } from '../../utils/videoDownload';
import { deleteProcessingVideo, deleteVideo } from '../../services/videoApi';
import { isInternalNetworkOnlyError } from '../../utils/internalNetworkError';
import { PROCESSING_DELETE_INTERNAL_ONLY } from '../../constants/accessMessages';
import { VISIBILITY_CHANGE_MESSAGES } from '../../utils/visibilityLabels';
import { toRelativePath } from '../../utils/videoImagePath';

/**
 * VideoCardSideコンポーネントのビジネスロジックを管理するカスタムフック
 * @param props コンポーネントのプロパティ
 * @returns ビジネスロジックの状態と操作関数
 */
export const useVideoCardSide = (props: VideoCardSideProps): UseVideoCardSideReturn => {
  const { video, onVisibilityChange, hideVisibilityToggle = false } = props;
  
  const showSnackbar = useSnackbar();
  const { startDownloadProgress, updateDownloadProgress, completeDownloadProgress } = useDownloadProgress();
  const navigate = useNavigate();
  const [hasBeenSwiped, setHasBeenSwiped] = useState<boolean>(false);
  const [isAutoSwipe, setIsAutoSwipe] = useState<boolean>(true);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  // 動画の可視性状態を内部ステートとして管理
  const [visibility, setVisibility] = useState<'public' | 'private' | 'hospital_only'>(
    (video.visibility as 'public' | 'private' | 'hospital_only') || 'private'
  );
  
  // ダイアログの状態管理
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogConfig, setDialogConfig] = useState<DialogConfig>({
    title: '',
    message: '',
    confirmText: '確認',
    type: 'info',
    onConfirm: () => {},
  });
  
  const swiperRef = useRef<SwiperType | null>(null);

  // SessionManager からアクティブセッションを取得
  const activeSession = SessionManager.getActiveSession();
  const activeUserId = activeSession ? activeSession.userId : null;
  const token = activeSession ? activeSession.token : null;

  // チャンネルラベル（病院名 - ユーザー名）
  const channelLabel =
    video.uploader?.hospital?.name && video.uploader?.name
      ? `${video.uploader.hospital.name}-${video.uploader.name}`
      : 'xxxxチャンネル';

  // ユーザーアイコン用URL（先頭スラッシュ正規化: icon_path の有無・スラッシュ有無を吸収）
  const uploaderIconUrl =
    video.uploader?.icon_path
      ? toRelativePath(video.uploader.icon_path)
      : '/asset/no_icon_user.png';

  // videoのvisibilityが変更されたら、内部ステートも更新
  useEffect(() => {
    if (video.visibility) {
      setVisibility(video.visibility as 'public' | 'private' | 'hospital_only');
    }
  }, [video.visibility]);

  // ロード時にお気に入り状態を確認
  // 処理中の動画（session_プレフィックス付きID）はスキップ
  useEffect(() => {
    if (activeUserId && token && video.id && !video.is_processing) {
      checkFavoriteStatus();
    }
  }, [video.id, activeUserId, token, video.is_processing]);

  // お気に入り状態をAPIでチェック
  const checkFavoriteStatus = async (): Promise<void> => {
    try {
      const response = await axios.get(`/api/favorites/check/${video.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.error('お気に入り状態の確認に失敗:', error);
      // エラーが発生してもUIを壊さないようにする（お気に入りボタンは通常表示のまま）
    }
  };

  // お気に入り登録/解除を行う
  const toggleFavorite = async (e: ReactMouseEvent): Promise<void> => {
    e.stopPropagation(); // カードのクリックイベントを停止

    // 処理中の動画はお気に入り操作を許可しない
    if (video.is_processing) {
      showSnackbar('処理中の動画はお気に入りに登録できません', SnackbarType.ERROR);
      return;
    }

    if (!activeUserId || !token) {
      showSnackbar('お気に入り登録にはログインが必要です', SnackbarType.ERROR);
      return;
    }
    
    try {
      if (isFavorite) {
        // お気に入り解除
        await axios.delete(`/api/favorites/${video.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
      } else {
        // お気に入り登録
        await axios.post(`/api/favorites`,
          { video_id: video.id },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('お気に入り操作でエラーが発生しました:', error);
      // より具体的なエラーメッセージを表示
      if (axios.isAxiosError(error) && error.response && error.response.status === 400) {
        // 400エラー（クライアントエラー）の場合は具体的なメッセージを表示
        showSnackbar(
          (error.response.data as { error: string }).error || 'お気に入り操作に失敗しました',
          SnackbarType.ERROR
        );
      } else {
        // その他のエラーの場合は一般的なメッセージ
        showSnackbar('サーバーとの通信に問題が発生しました。しばらく経ってからお試しください。', SnackbarType.ERROR);
      }
    }
  };

  // 公開状態を変更するダイアログを表示
  const showVisibilityChangeDialog = (e: ReactMouseEvent, newVisibility: 'public' | 'private' | 'hospital_only'): void => {
    e.stopPropagation(); // カードのクリックイベントを停止
    
    // 投稿者本人のみ変更可能
    if (video.uploader_id !== activeUserId) {
      showSnackbar('動画の公開状態は投稿者のみ変更できます', SnackbarType.ERROR);
      return;
    }

    if (newVisibility === 'public') {
      setDialogConfig({
        title: '動画を外部公開',
        message: 'この動画を外部公開に変更しますか？院外ユーザーからも閲覧可能になります。',
        confirmText: '外部公開にする',
        type: 'warning',
        onConfirm: () => changeVisibility('public'),
      });
    } else if (newVisibility === 'hospital_only') {
      setDialogConfig({
        title: '動画を院内公開',
        message: 'この動画を院内公開に変更しますか？同じ病院のユーザーのみ閲覧できます。',
        confirmText: '院内公開にする',
        type: 'info',
        onConfirm: () => changeVisibility('hospital_only'),
      });
    } else {
      setDialogConfig({
        title: '動画を非公開',
        message: 'この動画を非公開に変更しますか？',
        confirmText: '非公開にする',
        type: 'info',
        onConfirm: () => changeVisibility('private'),
      });
    }
    
    setDialogOpen(true);
  };

  // 公開状態を変更する処理
  const changeVisibility = async (newVisibility: 'public' | 'private' | 'hospital_only'): Promise<void> => {
    try {
      await axios.put(
        `/api/videos/${video.id}/visibility`,
        { visibility: newVisibility },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setVisibility(newVisibility);
      showSnackbar(
        VISIBILITY_CHANGE_MESSAGES[newVisibility],
        SnackbarType.SUCCESS
      );
      
      // 親コンポーネントに通知（コールバック関数が提供されている場合）
      if (typeof onVisibilityChange === 'function') {
        onVisibilityChange(video.id, newVisibility);
      }
    } catch (error) {
      console.error('公開状態の更新に失敗:', error);
      // 診療科未設定で公開しようとした場合など、サーバーが理由を返すときはそれを見せる
      showSnackbar(getServerErrorMessage(error) ?? '公開状態の更新に失敗しました', SnackbarType.ERROR);
    }
  };

  // カード全体クリック => 動画詳細ページへ遷移
  const handleContainerClick = (e: ReactMouseEvent): void => {
    // ダイアログが開いているときは遷移しない
    if (dialogOpen) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    // スライダーの矢印・ドットの場合はナビゲーションのみを実施
    const target = e.target as HTMLElement;
    if (
      target.closest('.swiper-button-next') ||
      target.closest('.swiper-button-prev') ||
      target.closest('.swiper-pagination-bullet')
    ) {
      setHasBeenSwiped(true);
      setIsAutoSwipe(false);
      e.stopPropagation();
      return;
    }
    if (video.isExternal && video.external_id) {
      navigate(`/external-videos/${video.external_id}`);
    } else {
      navigate(`/videos/${video.id}`);
    }
  };

  // ユーザーの手動スワイプを検出
  const handleSlideChange = (swiper: SwiperType): void => {
    // autoplayによるスライド変更は無視する
    if (!isAutoSwipe && !hasBeenSwiped) {
      setHasBeenSwiped(true);
    }
  };

  // タッチイベント検出
  const handleTouchStart = (): void => {
    setIsAutoSwipe(false);
    if (!hasBeenSwiped) {
      setHasBeenSwiped(true);
    }
  };

  // ユーザー操作によるナビゲーション (矢印ボタンなど) の検出
  const handleNavigation = (): void => {
    setIsAutoSwipe(false);
    if (!hasBeenSwiped) {
      setHasBeenSwiped(true);
    }
  };

  // スワイプモードの自動検出
  const handleAutoplayStart = (): void => {
    setIsAutoSwipe(true);
  };

  // 処理中の動画はuploader_idで判定、通常動画はuploader.idで判定
  const isOwner = video.uploader?.id === activeUserId || video.uploader_id === activeUserId;
  
  // 画像データをdisplay_orderでソート
  const [sortedImages, setSortedImages] = useState<VideoImage[] | null>(null);
  
  // 画像データがアップデートされたら再ソート
  useEffect(() => {
    if (video.images && video.images.length > 0) {
      const sorted = [...video.images].sort((a, b) => {
        // display_orderが存在する場合はそれでソート（nullチェックも厳密に行う）
        if (a.display_order !== undefined && a.display_order !== null &&
            b.display_order !== undefined && b.display_order !== null) {
          return a.display_order - b.display_order;
        }
        
        // display_orderが片方だけある場合は、あるほうを優先
        if (a.display_order !== undefined && a.display_order !== null) return -1;
        if (b.display_order !== undefined && b.display_order !== null) return 1;
        
        // display_orderがない場合はidでソート（降順）
        if (a.id && b.id) {
          return String(b.id).localeCompare(String(a.id));
        }
        
        // どちらも使えない場合は作成日時でソート
        return new Date(a.created_at || '0').getTime() - new Date(b.created_at || '0').getTime();
      });
      
      setSortedImages(sorted);
    } else {
      setSortedImages(null);
    }
  }, [video.images]);
  
  const hasMultipleImages = sortedImages !== null && sortedImages.length > 1;
  
  // 非公開動画かどうかを判定
  const isPrivate = visibility === 'private';

  // ダウンロードハンドラー
  const handleDownload = useCallback(async (e: ReactMouseEvent): Promise<void> => {
    e.stopPropagation();
    const defaultFileName = video.title || video.name || 'video';
    
    await downloadVideo(
      video.id,
      token || '',
      defaultFileName,
      undefined,
      (errorMessage) => {
        showSnackbar(errorMessage, SnackbarType.ERROR);
      },
      {
        startDownloadProgress,
        updateDownloadProgress,
        completeDownloadProgress
      }
    );
  }, [video.id, video.title, video.name, token, showSnackbar, startDownloadProgress, updateDownloadProgress, completeDownloadProgress]);

  // 動画削除ハンドラー（処理中動画と通常動画の両方に対応）
  const handleDelete = useCallback(async (e: ReactMouseEvent): Promise<void> => {
    e.stopPropagation();

    // 処理中動画の判定: is_processingフラグまたはIDがsession_で始まる場合
    const videoIdStr = String(video.id);
    const isProcessing = video.is_processing || videoIdStr.startsWith('session_');

    // 削除確認ダイアログを表示
    setDialogConfig({
      title: isProcessing ? '変換中の動画を削除' : '動画を削除',
      message: isProcessing
        ? 'この動画の変換処理をキャンセルして削除しますか？この操作は取り消せません。'
        : 'この動画を削除しますか？この操作は取り消せません。',
      confirmText: '削除する',
      type: 'warning',
      onConfirm: async () => {
        try {
          if (isProcessing) {
            await deleteProcessingVideo(videoIdStr);
          } else {
            await deleteVideo(videoIdStr);
          }
          showSnackbar('動画を削除しました', SnackbarType.SUCCESS);
          // ページをリロードして一覧を更新
          window.location.reload();
        } catch (error) {
          console.error('動画の削除に失敗:', error);
          // 院外から処理中動画を削除しようとした場合は 403 INTERNAL_NETWORK_ONLY が返る。
          // 汎用文言で潰さず、院内限定である旨の専用文言を出す（download / upload と同じ扱い）。
          // isProcessing との AND を取るのは、この文言が処理中動画専用だから
          // （accessMessages.ts の PROCESSING_DELETE_INTERNAL_ONLY 参照）。
          // 将来ゲート適用範囲が通常削除に広がっても、誤って「変換中の…」と案内しない。
          showSnackbar(
            isProcessing && isInternalNetworkOnlyError(error)
              ? PROCESSING_DELETE_INTERNAL_ONLY
              : '動画の削除に失敗しました',
            SnackbarType.ERROR
          );
        }
      },
    });
    setDialogOpen(true);
  }, [video.id, video.is_processing, showSnackbar]);

  // ケバブメニューの状態管理（共通フックを使用）
  const kebabMenu: UseKebabMenuReturn = useKebabMenu({
    video,
    isFavorite,
    isPrivate,
    toggleFavorite,
    showVisibilityChangeDialog,
    navigate,
    leftOffset: UI.KEBAB_MENU_LEFT_OFFSET.SIDE_AND_MOBILE,
    onDownload: handleDownload,
    onDelete: handleDelete
  });

  return {
    hasBeenSwiped,
    isAutoSwipe,
    isFavorite,
    visibility,
    dialogOpen,
    dialogConfig,
    swiperRef,
    activeUserId,
    token,
    uploaderIconUrl,
    channelLabel,
    isOwner,
    isPrivate,
    sortedImages,
    hasMultipleImages,
    
    toggleFavorite,
    showVisibilityChangeDialog,
    changeVisibility,
    handleContainerClick,
    handleSlideChange,
    handleTouchStart,
    handleNavigation,
    handleAutoplayStart,
    setHasBeenSwiped,
    setIsAutoSwipe,
    setDialogOpen,
    // ケバブメニュー関連（共通フックから取得）
    kebabMenu,
  };
}; 
